import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { formatStudentCount } from "@/helpers/formatNumber";
import { generateAvatarProps } from "@/helpers/avatarColors";
import { computeYearsSinceFounding } from "@/helpers/computeYearsSinceFounding";

export default function WOL(props) {
    // Le nombre vient du parent quand il sait le lire sur le back ; sinon la
    // valeur figée de la configuration, arrondie, fait l'affaire.
    const nombreCommunaute =
        props.nombreComptes || Number(process.env.NUMBER_OF_STUDENTS);
    const t = useTranslations();
    const yearsExistence = computeYearsSinceFounding();

    const testimonials = [
        {
            id: 1,
            text: t("wol_testimonial1"),
            author: "Benjamin C.",
            role: t("wol_webDeveloper"),
        },
        {
            id: 2,
            text: t("wol_testimonial2"),
            author: "Guillaume Morand",
            role: t("wol_projectManager"),
        },
        {
            id: 3,
            text: t("wol_testimonial3"),
            author: "Anais Revellat",
            role: t("wol_frontendDeveloper"),
        },
        {
            id: 4,
            text: t("wol_testimonial4"),
            author: "Léa Dubois",
            role: t("wol_productManager"),
        },
        {
            id: 5,
            text: t("wol_testimonial5"),
            author: "Alain Tardif",
            role: t("wol_leadDeveloper"),
        },
        {
            id: 6,
            text: t("wol_testimonial6"),
            author: "Gaultier Martin",
            role: t("wol_devOpsEngineer"),
        },
    ];

    const stats = [
        {
            value: formatStudentCount(nombreCommunaute),
            label: t("wol_students"),
        },
        {
            value: `${process.env.TRUSTPILOT_NOTE}/5`,
            label: t("wol_averageRating"),
        },
        {
            value: `${process.env.BELIEVEMY_SATISFACTION_RATE}%`,
            label: t("wol_satisfactionRate"),
        },
        {
            value: t("wol_yearsExistence_value", { count: yearsExistence }),
            label: t("wol_yearsExistence"),
        },
        {
            value: "Qualiopi",
            label: t("wol_qualiopi"),
        },
    ];

    return (
        <section className={`${props.className} py-20 lg:py-28`}>
            <div
                className={`${
                    props.withoutContainer
                        ? ""
                        : "container mx-auto px-4 xl:w-10/12 w-11/12"
                }`}
            >
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
                    <span className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                        <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {t("wol_title")}
                    </span>
                    <h2
                        className="text-3xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight"
                        dangerouslySetInnerHTML={{
                            __html: t.raw("wol_titleTwo"),
                        }}
                    />
                    <p className="text-gray-600 dark:text-grayDark-400 text-lg">
                        {t("wol_subtitle", {
                            numberOfStudents: formatStudentCount(nombreCommunaute),
                        })}
                    </p>
                </div>

                {/* Stats Row */}
                <div className="flex flex-wrap justify-center gap-6 lg:gap-12 mb-12 lg:mb-16">
                    {stats.map((stat, index) => (
                        <div key={index} className="text-center">
                            <div className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                                {stat.value}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-grayDark-500">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Testimonials Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mb-12 lg:mb-16">
                    {testimonials.map((testimonial) => {
                        const avatarProps = generateAvatarProps(
                            testimonial.author,
                            "sm",
                        );

                        return (
                            <div
                                key={testimonial.id}
                                className="group relative flex flex-col bg-white dark:bg-grayDark-900 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)] dark:shadow-none border border-gray-200/60 dark:border-grayDark-800 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:hover:border-grayDark-700 hover:border-gray-300/80 transition-all duration-300"
                            >
                                {/* Quote icon */}
                                <svg
                                    className="w-8 h-8 text-blue-500/20 dark:text-blue-400/20 mb-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                </svg>

                                {/* Quote */}
                                <blockquote className="text-gray-600 dark:text-grayDark-300 leading-relaxed mb-6 text-[15px] flex-grow">
                                    {testimonial.text}
                                </blockquote>

                                {/* Author + Rating */}
                                <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-grayDark-800 mt-auto">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`${avatarProps.className} ring-2 ring-white dark:ring-grayDark-900`}
                                            style={avatarProps.style}
                                        >
                                            {avatarProps.initials}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white text-sm mb-0">
                                                {testimonial.author}
                                            </p>
                                            <p className="text-gray-400 dark:text-grayDark-500 text-xs mb-0">
                                                {testimonial.role}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <svg
                                                key={i}
                                                className="w-3.5 h-3.5 text-amber-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Trustpilot */}
                <div className="flex flex-col items-center mb-20 lg:mb-28">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                                <svg
                                    key={i}
                                    className="w-5 h-5 text-[#00b67a]"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {process.env.TRUSTPILOT_NOTE}/5
                        </span>
                    </div>
                    <Link
                        href="https://fr.trustpilot.com/review/believemy.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t("wol_trustpilot_link")}
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 dark:text-grayDark-400 dark:hover:text-white transition-colors text-sm"
                    >
                        <Image
                            src="/images/site/assets/avis.png"
                            alt=""
                            width={100}
                            height={24}
                            className="opacity-80 hover:opacity-100 transition-opacity"
                        />
                    </Link>
                </div>
            </div>
        </section>
    );
}
